import type { CSSProperties } from "react";
import type { TFunction } from "i18next";
import {
  BEVEL_CHIP_ON,
  BEVEL_INSET_NAVY,
  BEVEL_INSET_WHITE,
  BEVEL_NAVY_FACE,
  BEVEL_WHITE_FACE,
  CONTROL_EDGE,
  CONTROL_LIT,
} from "@majourneys/shared/ui";
import { latnLocale } from "../../../../lib/format";
import { panelStyle } from "../../../../shared/ui/card-frame";

/**
 * The chat kit's vocabulary (approved kit, C20 to C28). One place, so the
 * interactive templates cannot drift from each other or from the lab.
 */
export const KIT = {
  navy: "#110E39",
  gold: "#E5B265",
  goldDark: "#8A6425",
  muted: "#5a5d77",
  green: "#1F7A4D",
  greenTint: "rgba(31,122,77,0.14)",
  pink: "#B03A6E",
  pinkTint: "rgba(219,95,148,0.16)",
  goldTint: "rgba(229,178,101,0.2)",
  softTint: "rgba(17,14,57,0.06)",
  hairline: "rgba(17,14,57,0.08)",
  navyGradient: "linear-gradient(135deg, #2E2878 0%, #1A1454 48%, #110E39 100%)",
} as const;

/** A control at rest: lit white and one hairline. */
export const CONTROL: CSSProperties = {
  background: CONTROL_LIT,
  boxShadow: `inset 0 0 0 1px ${CONTROL_EDGE}`,
};

/** The chosen control: the navy ramp the primary button wears. */
export const CHOSEN: CSSProperties = {
  background: BEVEL_NAVY_FACE,
  color: "#fff",
  // A chosen chip casts no drop shadow (owner, 2026-10-11).
  boxShadow: BEVEL_CHIP_ON,
};

/** A white button or chip you press: the soft-bevel face over the same hairline. */
export const BUTTON: CSSProperties = {
  background: BEVEL_WHITE_FACE,
  boxShadow: `${BEVEL_INSET_WHITE}, inset 0 0 0 1px ${CONTROL_EDGE}`,
};

export const PRIMARY: CSSProperties = {
  background: BEVEL_NAVY_FACE,
  color: "#fff",
  boxShadow: `0 10px 24px -12px rgba(17,14,57,0.8), ${BEVEL_INSET_NAVY}`,
};

/** Reading surfaces wear the band: the house panel at the kit's radius. */
export const KIT_RADIUS = 18;
export function kitPanel(): CSSProperties {
  return panelStyle(KIT_RADIUS) as CSSProperties;
}

/** "2 adults", "2 adults and 1 child", in the chat's language. */
export function partyPhrase(t: TFunction, adults: number, children = 0): string {
  const a = t("ai_concierge.kit.party.adults", { count: adults });
  if (children <= 0) return a;
  return t("ai_concierge.kit.party.with_children", {
    adults: a,
    children: t("ai_concierge.kit.party.children", { count: children }),
  });
}

/** A YYYY-MM-DD as a UTC Date at noon, so no zone can tip it a day over. */
export function isoToUtcNoon(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1, 12));
}

/** "Saturday 10 October" in the chat language, Latin digits in every locale. */
export function longDate(iso: string, lang: string): string {
  try {
    return new Intl.DateTimeFormat(latnLocale(lang), {
      weekday: "long",
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    }).format(isoToUtcNoon(iso));
  } catch {
    return iso;
  }
}

/** "Sat 10 Oct" in the chat language. */
export function shortDate(iso: string, lang: string): string {
  try {
    return new Intl.DateTimeFormat(latnLocale(lang), {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }).format(isoToUtcNoon(iso));
  } catch {
    return iso;
  }
}

/** The fixed-language t for the chat, so what we send matches the chat. */
export function chatT(
  i18n: { getFixedT?: (lng: string) => TFunction } | undefined,
  t: TFunction,
  lang: string | undefined,
): TFunction {
  if (lang && typeof i18n?.getFixedT === "function") {
    try {
      return i18n.getFixedT(lang);
    } catch {
      return t;
    }
  }
  return t;
}
